/**
 * NotificationCard — single notification row used across:
 *   • NotificationBell dropdown (compact)
 *   • Notification center pages (full)
 *
 * Props:
 *   notification  – the notification object
 *   compact       – boolean (dropdown mode vs. full page mode)
 *   onMarkRead    – (id) => void
 *   onDelete      – (id) => void
 */

import { motion }  from 'framer-motion'
import { Trash2, CheckCheck, Circle, CheckCircle2 } from 'lucide-react'

import { cn, formatRelativeTime } from '@utils'
import { NOTIFICATION_TYPE_CONFIG } from '../schemas/notificationSchemas'

// Icon per notification type
function TypeDot({ type }) {
  const cfg = NOTIFICATION_TYPE_CONFIG[type] ?? NOTIFICATION_TYPE_CONFIG.info
  return (
    <div
      className="w-2 h-2 rounded-full flex-shrink-0"
      style={{ background: cfg.border }}
    />
  )
}

export function NotificationCard({ notification, compact = false, onMarkRead, onDelete }) {
  const cfg     = NOTIFICATION_TYPE_CONFIG[notification.type] ?? NOTIFICATION_TYPE_CONFIG.info
  const isUnread = !notification.isRead

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: -20, transition: { duration: 0.2 } }}
      whileHover={{ backgroundColor: 'var(--ac-bg-subtle)', transition: { duration: 0.1 } }}
      className={cn(
        'relative flex gap-3 transition-colors duration-150 cursor-default group',
        compact ? 'px-4 py-3' : 'px-5 py-4 rounded-2xl border',
        !compact && 'hover:shadow-sm',
      )}
      style={
        compact
          ? {}
          : {
              background:   isUnread ? `${cfg.bg}` : 'var(--ac-bg-surface)',
              borderColor:  isUnread ? cfg.border  : 'var(--ac-border-base)',
              boxShadow:    'var(--ac-shadow-sm)',
            }
      }
    >
      {/* Unread indicator bar (full mode) */}
      {!compact && isUnread && (
        <span
          className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-8 rounded-r-full"
          style={{ background: cfg.border }}
        />
      )}

      {/* Type icon / dot */}
      <div className="flex-shrink-0 pt-0.5">
        {compact ? (
          <TypeDot type={notification.type} />
        ) : (
          <div
            className="w-8 h-8 rounded-xl flex items-center justify-center"
            style={{ background: cfg.bg }}
          >
            <TypeDot type={notification.type} />
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p
          className={cn(
            'text-sm leading-snug',
            isUnread ? 'font-semibold' : 'font-medium',
          )}
          style={{ color: 'var(--ac-text-base)' }}
        >
          {notification.title}
        </p>
        <p
          className={cn(
            'text-xs mt-0.5 leading-relaxed',
            compact ? 'line-clamp-1' : 'line-clamp-2',
          )}
          style={{ color: 'var(--ac-text-muted)' }}
        >
          {notification.message}
        </p>
        <p
          className="text-xs mt-1"
          style={{ color: 'var(--ac-text-subtle)' }}
        >
          {formatRelativeTime(notification.createdAt)}
        </p>
      </div>

      {/* Actions (visible on hover in full mode, always in compact) */}
      <div
        className={cn(
          'flex items-start gap-1 flex-shrink-0',
          !compact && 'opacity-0 group-hover:opacity-100 transition-opacity duration-150',
        )}
      >
        {isUnread && onMarkRead && (
          <button
            type="button"
            title="Mark as read"
            onClick={(e) => { e.stopPropagation(); onMarkRead(notification.id) }}
            className="p-1.5 rounded-lg transition-colors duration-150 hover:bg-[var(--ac-bg-subtle)]"
            style={{ color: 'var(--ac-text-subtle)' }}
          >
            <CheckCircle2 size={13} strokeWidth={2} />
          </button>
        )}
        {onDelete && !compact && (
          <button
            type="button"
            title="Delete"
            onClick={(e) => { e.stopPropagation(); onDelete(notification.id) }}
            className="p-1.5 rounded-lg transition-colors duration-150 hover:bg-[var(--ac-danger-bg)]"
            style={{ color: 'var(--ac-text-subtle)' }}
          >
            <Trash2 size={13} strokeWidth={2} />
          </button>
        )}
        {/* Unread dot indicator in compact mode */}
        {compact && isUnread && (
          <div
            className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0"
            style={{ background: cfg.border }}
          />
        )}
      </div>
    </motion.div>
  )
}
